import { useEffect, useRef, useState } from 'react';
import { SlidersHorizontal } from '@phosphor-icons/react';
import { errorStatus } from '../lib/error';
import { formatPrice, humanize } from '../lib/format';
import { useSearchUrlState } from '../hooks/useSearchUrlState';
import { useSearch } from '../hooks/useSearch';
import { PropertyCard } from '../components/landing/PropertyCard';
import { SearchSummary } from '../components/search/SearchSummary';
import { FiltersBody } from '../components/search/FiltersBody';
import { FilterSheet } from '../components/search/FilterSheet';
import { ActiveFilterChips, type FilterChip } from '../components/search/ActiveFilterChips';
import { ResultsSkeleton, EmptyState, ErrorState } from '../components/search/SearchStates';

/**
 * Search results (/search) — top of the booking funnel. URL params drive the query
 * (useSearchUrlState); results come from GET /v1/public/search via useSearch
 * (useInfiniteQuery + keepPreviousData). See docs/search-results/.
 */
export default function SearchResultsPage() {
  const { state, patch, clearFilters } = useSearchUrlState();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);

  const params = {
    destination: state.destination || undefined,
    checkIn: state.checkIn || undefined,
    checkOut: state.checkOut || undefined,
    guests: state.guests,
    priceMin: state.priceMin,
    priceMax: state.priceMax,
    amenities: state.amenities.length ? state.amenities.join(',') : undefined,
    sort: state.sort,
  };

  const {
    data, isPending, isError, error, refetch,
    fetchNextPage, hasNextPage, isFetchingNextPage, isFetching,
  } = useSearch(params);

  const allStays = data?.pages.flatMap((p) => p.data) ?? [];
  const stays = minRating > 0 ? allStays.filter((s) => s.rating >= minRating) : allStays;
  const facets = data?.pages[0]?.facets;
  const serverTotal = data?.pages[0]?.meta.total ?? 0;

  // Rating is a client-only filter (the server has no rating param), so with it
  // active we must pull the *whole* result set before the count/filter can be
  // trusted — otherwise matching hotels on unfetched pages are silently hidden
  // and the header count reflects only page 1. Auto-page through the rest.
  const ratingPagesPending = minRating > 0 && (hasNextPage ?? false);
  useEffect(() => {
    if (minRating > 0 && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [minRating, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const total = minRating > 0 ? (ratingPagesPending ? serverTotal : stays.length) : serverTotal;
  const currency = allStays[0]?.currency ?? 'USD';
  const hasFilters =
    state.priceMin != null || state.priceMax != null || state.amenities.length > 0 || minRating > 0;
  const place = state.destination.trim();

  function clearAll() {
    clearFilters();
    setMinRating(0);
  }

  const chips: FilterChip[] = [];
  if (state.priceMin != null || state.priceMax != null) {
    const lo = state.priceMin != null ? formatPrice(state.priceMin, currency) : 'min';
    const hi = state.priceMax != null ? formatPrice(state.priceMax, currency) : 'max';
    chips.push({ key: 'price', label: `${lo} – ${hi}` });
  }
  for (const a of state.amenities) chips.push({ key: `amenity:${a}`, label: humanize(a) });

  function removeChip(key: string) {
    if (key === 'price') patch({ priceMin: undefined, priceMax: undefined });
    else if (key.startsWith('amenity:')) {
      const a = key.slice('amenity:'.length);
      patch({ amenities: state.amenities.filter((x) => x !== a) });
    }
  }

  function loadMore() {
    const prevCount = stays.length;
    fetchNextPage().then(() => {
      requestAnimationFrame(() => {
        gridRef.current
          ?.querySelector<HTMLElement>(`[data-card-index="${prevCount}"] a`)
          ?.focus();
      });
    });
  }

  const filters = (
    <FiltersBody
      facets={facets}
      price={{ min: state.priceMin, max: state.priceMax }}
      amenities={state.amenities}
      minRating={minRating}
      currency={currency}
      onPriceChange={(p) => patch({ priceMin: p.min, priceMax: p.max })}
      onAmenitiesChange={(a) => patch({ amenities: a })}
      onRatingChange={setMinRating}
    />
  );

  return (
    <div className="min-h-screen bg-cream px-5 py-8 sm:px-8 lg:px-14 2xl:px-20">
      <SearchSummary
        destination={state.destination}
        checkIn={state.checkIn}
        checkOut={state.checkOut}
        guests={state.guests}
        total={total}
        isFetching={isFetching}
        sort={state.sort}
        onSearch={(s) => patch(s)}
        onSortChange={(sort) => patch({ sort })}
      />

      {chips.length > 0 && (
        <div className="mt-4">
          <ActiveFilterChips chips={chips} onRemove={removeChip} onClearAll={clearAll} />
        </div>
      )}

      {/* Mobile: open filters sheet */}
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="mt-4 inline-flex items-center gap-2 rounded-full border border-sand bg-white px-4 py-2 text-sm font-semibold text-ink transition hover:bg-cream-deep lg:hidden"
      >
        <SlidersHorizontal className="h-4 w-4 text-clay" /> Filters
      </button>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
        {/* Desktop rail */}
        <aside className="hidden lg:block">
          <div className="sticky top-6 rounded-2xl border border-sand bg-white p-6 shadow-[0_24px_50px_-36px_rgba(28,26,23,0.4)]">
            <div className="mb-5 flex items-center justify-between border-b border-sand pb-4">
              <h2 className="font-serif text-xl font-bold text-ink">Filters</h2>
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-sm font-semibold text-clay hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>
            {filters}
          </div>
        </aside>

        {/* Results */}
        <div ref={gridRef}>
          {isPending ? (
            <ResultsSkeleton />
          ) : isError ? (
            <ErrorState onRetry={() => refetch()} throttled={errorStatus(error) === 429} />
          ) : stays.length === 0 && !ratingPagesPending ? (
            <EmptyState hasFilters={hasFilters} place={place} onClearFilters={clearAll} />
          ) : (
            <>
              <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 ${isFetching ? 'opacity-60 transition-opacity' : ''}`}>
                {stays.map((stay, i) => (
                  <div key={`${stay.id}-${i}`} data-card-index={i}>
                    <PropertyCard stay={stay} />
                  </div>
                ))}
              </div>

              <div className="mt-8 flex justify-center">
                {hasNextPage ? (
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={isFetchingNextPage}
                    className="rounded-full bg-clay px-7 py-3 font-semibold text-white transition hover:bg-clay-dark disabled:opacity-70"
                  >
                    {isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </button>
                ) : (
                  <p className="text-sm text-ink-muted">You've reached the end.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <FilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        total={total}
        onClearAll={clearAll}
      >
        {filters}
      </FilterSheet>
    </div>
  );
}
